import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, Link } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { ArrowLeftIcon, DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-toastify'
import { PageHeader } from 'src/components/layout/page-header'
import { Spinner } from 'src/components/spinner'
import { Button } from 'src/components/button'
import { ConfirmDeleteDialog } from 'src/components/confirm-delete-dialog'
import { PropertyBasicInfoCard } from 'src/components/properties/property-basic-info-card'
import { InboundCalendarsCard } from 'src/components/properties/inbound-calendars-card'
import { CalendarSyncCard } from 'src/components/properties/calendar-sync-card'
import { PropertyReportingSettings } from 'src/components/properties/property-reporting-settings'
import { CoOnboardingBanner } from 'src/components/properties/co-onboarding-banner'
import { CoSetupWizard } from 'src/components/properties/co-setup-wizard'
import { DuplicatePropertyModal } from 'src/components/properties/duplicate-property-modal'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { propertyApi } from 'src/services/properties/api'
import { propertyQueries } from 'src/services/properties/queries'

export function PropertyDetailView() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [showWizard, setShowWizard] = useState(false)
  const [showDuplicate, setShowDuplicate] = useState(false)

  const {
    data: property,
    isLoading,
    isError,
    error,
  } = useQuery({
    ...propertyQueries.retrieve(id!),
    enabled: Boolean(id),
  })

  const { requestDelete, dialogProps } = useDeleteWithConfirmation({
    mutationFn: propertyApi.remove,
    queryKey: propertyQueries.all(),
    onSuccess: () => navigate('/properties'),
  })

  // Handle 404 / not-found by redirecting to the list with a toast.
  useEffect(() => {
    if (!isLoading && isError) {
      if (isAxiosError(error) && error.response?.status === 404) {
        toast.error(t('propertyForm.notFoundToast'))
        navigate('/properties', { replace: true })
      }
    }
  }, [isLoading, isError, error, navigate, t])

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!property) {
    return null
  }

  const displayName = property.name || t('propertiesPage.unnamedProperty')
  const subtitle = [property.city, property.country].filter(Boolean).join(', ')

  return (
    <>
      <PageHeader>
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <nav className="flex items-center gap-1 text-sm text-text-placeholder">
              <Link
                to="/properties"
                className="flex items-center gap-1 transition-colors hover:text-primary"
              >
                <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
                {t('propertyForm.allProperties')}
              </Link>
              <span className="mx-1">&gt;</span>
              <span className="text-gray-700">{displayName}</span>
            </nav>
            <h1 className="text-2xl font-bold text-primary">{displayName}</h1>
            {subtitle && <p className="text-sm text-text-placeholder">{subtitle}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              iconOnly
              tooltip={t('propertyForm.duplicateProperty')}
              onClick={() => setShowDuplicate(true)}
              data-testid="duplicate-property-button"
            >
              <DocumentDuplicateIcon className="h-5 w-5" aria-hidden="true" />
            </Button>
            <Button
              variant="danger"
              iconOnly
              tooltip={t('propertyForm.deleteProperty')}
              onClick={() => requestDelete(id!, displayName)}
              data-testid="delete-property-button"
            >
              <TrashIcon className="h-5 w-5" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </PageHeader>

      <div className="mt-6">
        <CoOnboardingBanner property={property} onSetupClick={() => setShowWizard(true)} />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Left column */}
        <div className="flex flex-col gap-6">
          <PropertyBasicInfoCard property={property} />
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-6">
          <InboundCalendarsCard property={property} />
          <CalendarSyncCard property={property} />
        </div>
      </div>

      <div className="mt-6">
        <PropertyReportingSettings property={property} />
      </div>

      <CoSetupWizard isOpen={showWizard} onClose={() => setShowWizard(false)} property={property} />

      <DuplicatePropertyModal
        isOpen={showDuplicate}
        onClose={() => setShowDuplicate(false)}
        propertyId={property.id}
      />

      <ConfirmDeleteDialog {...dialogProps} />
    </>
  )
}
